import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  type ContentSearchParams,
  parseContentSearch,
  searchQuery,
} from '@/admin/contentSearch';
import { LANGUAGE_LABELS } from '@/admin/languages';
import { CourseHeader, TabHeading } from '@/admin/ui/ContentNav';
import { SearchFilters } from '@/admin/ui/ContentSearch';
import { PageTitle } from '@/admin/ui/Page';
import { LIST_PAGE_SIZE, Pagination } from '@/admin/ui/Pagination';
import { Table } from '@/admin/ui/Table';
import { NeedsNewVersion } from '@/admin/ui/Versions';
import { CreateLessonButton } from '@/admin/widgets/CreateLessonButton';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import i18n from '~/i18n';

type LessonAdminSummary = components['schemas']['LessonAdminSummary'];
type LessonSearchItem = components['schemas']['LessonSearchItem'];

export const Route = createFileRoute('/admin/lessons/')({
  validateSearch: (
    s: Record<string, unknown>,
  ): { course?: number; version?: number } & ContentSearchParams => ({
    // With a course, its lessons; without, a search across every course.
    course: s.course != null ? Number(s.course) : undefined,
    version: s.version != null ? Number(s.version) : undefined,
    ...parseContentSearch(s),
  }),
  component: LessonsPage,
});

function LessonsPage() {
  const { course, version } = Route.useSearch();
  return course !== undefined && version !== undefined ? (
    <CourseLessons course={course} version={version} />
  ) : (
    <LessonSearch />
  );
}

const columnHelper = createColumnHelper<LessonAdminSummary>();

const columns = [
  columnHelper.accessor('order', {
    header: () => i18n.t('admin.lessons.col.order'),
  }),
  columnHelper.accessor('id', { header: () => i18n.t('admin.common.id') }),
  columnHelper.accessor('title', {
    header: () => i18n.t('admin.common.title'),
  }),
  columnHelper.accessor('status', {
    header: () => i18n.t('admin.common.status'),
  }),
];

function CourseLessons({
  course,
  version,
}: {
  course: number;
  version: number;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);

  // Admin endpoint: all statuses (the student course_lessons is gated to a
  // fully published path).
  const { data, isLoading, error } = api.adminListLessons.useQuery({
    path: { course_id: course },
    query: { version, limit: LIST_PAGE_SIZE, offset: LIST_PAGE_SIZE * page },
  });
  const courseQuery = api.adminGetCourse.useQuery({
    path: { course_id: course },
    query: { version },
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) return <Message>{t('admin.lessons.loading')}</Message>;
  if (error) return <Message $error>{t('admin.lessons.errorLoading')}</Message>;

  const pagination = (hideWhenAlone = false) => (
    <Pagination
      page={page}
      pageSize={LIST_PAGE_SIZE}
      shown={data?.data.length ?? 0}
      total={data?.count}
      onPage={setPage}
      hideWhenAlone={hideWhenAlone}
    />
  );

  return (
    <div>
      {courseQuery.data && <CourseHeader course={courseQuery.data} />}
      <TabHeading
        action={
          courseQuery.data?.version_state === 'draft' && (
            <CreateLessonButton courseId={course} version={version} />
          )
        }
      >
        {t('admin.nav.lessons')}
      </TabHeading>
      {courseQuery.data && courseQuery.data.version_state !== 'draft' && (
        <NeedsNewVersion target={{ courseId: course, version }}>
          {t('admin.lessons.lockedNotice')}
        </NeedsNewVersion>
      )}
      {pagination()}

      <Table
        table={table}
        to="/admin/lessons/$lessonId/"
        getParams={(row) => ({ lessonId: String(row.id) })}
      />
      {pagination(true)}
    </div>
  );
}

const searchColumnHelper = createColumnHelper<LessonSearchItem>();

const searchColumns = [
  searchColumnHelper.accessor('id', {
    header: () => i18n.t('admin.common.id'),
  }),
  searchColumnHelper.accessor('title', {
    header: () => i18n.t('admin.common.title'),
  }),
  searchColumnHelper.accessor('course_title', {
    header: () => i18n.t('admin.lessons.col.course'),
  }),
  searchColumnHelper.accessor('language', {
    header: () => i18n.t('admin.common.language'),
    cell: (info) => i18n.t(LANGUAGE_LABELS[info.getValue()]),
  }),
  searchColumnHelper.accessor('status', {
    header: () => i18n.t('admin.common.status'),
  }),
];

function LessonSearch() {
  const { t } = useTranslation();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const page = (params.page ?? 1) - 1;

  const { data, isLoading, error } = api.adminSearchLessons.useQuery({
    query: searchQuery(params, LIST_PAGE_SIZE),
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns: searchColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const update = (patch: ContentSearchParams, replace = true) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace });

  const pagination = (hideWhenAlone = false) => (
    <Pagination
      page={page}
      pageSize={LIST_PAGE_SIZE}
      shown={data?.data.length ?? 0}
      total={data?.count}
      onPage={(next) =>
        update({ page: next > 0 ? next + 1 : undefined }, false)
      }
      hideWhenAlone={hideWhenAlone}
    />
  );

  return (
    <div>
      <PageTitle>{t('admin.nav.lessons')}</PageTitle>
      <Intro>{t('admin.lessons.intro')}</Intro>
      <SearchFilters
        params={params}
        onChange={update}
        placeholder={t('admin.lessons.searchPlaceholder')}
      />
      {isLoading ? (
        <Message>{t('admin.lessons.loading')}</Message>
      ) : error ? (
        <Message $error>{t('admin.lessons.errorLoading')}</Message>
      ) : data?.count === 0 ? (
        <Message>{t('admin.lessons.noMatch')}</Message>
      ) : (
        <>
          {pagination()}
          <Table
            table={table}
            to="/admin/lessons/$lessonId/"
            getParams={(row) => ({ lessonId: String(row.id) })}
          />
          {pagination(true)}
        </>
      )}
    </div>
  );
}

const Intro = styled.p`
  color: var(--grey-700);
  font-size: 0.875rem;
  margin: 0.25rem 0 0;
`;

const Message = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;
